import {
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@roberttamas/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { DietaryChips } from "@/components/dietary-chips";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
} from "@/components/icons";
import { EditDialog, SettingRow } from "@/components/setting-row";
import { updateSiteConfig } from "@/lib/api";
import { makeId } from "@/lib/draft-ids";
import { toAppError } from "@/lib/errors";
import {
  DEFAULT_MENU_ICON,
  MENU_ICON_OPTIONS,
  MENU_MAX_GROUPS,
  MENU_MAX_ITEMS,
  getMenuIcon,
} from "@/lib/menu";
import type {
  DietaryTag,
  MenuConfig,
  MenuIconKey,
  MenuItem,
} from "@/lib/types";

interface DraftItem {
  id: string;
  name: string;
  description: string;
  dietary: DietaryTag[];
}

interface DraftGroup {
  id: string;
  name: string;
  time: string;
  icon: MenuIconKey;
  items: DraftItem[];
}


function blankItem(): DraftItem {
  return { id: makeId(), name: "", description: "", dietary: [] };
}

function blankGroup(): DraftGroup {
  return {
    id: makeId(),
    name: "",
    time: "",
    icon: DEFAULT_MENU_ICON,
    items: [blankItem()],
  };
}

function toDraftItems(items: MenuItem[] | undefined): DraftItem[] {
  return (items ?? []).map((it) => ({
    id: it.id,
    name: it.name,
    description: it.description ?? "",
    dietary: it.dietary ?? [],
  }));
}

function toDraftGroups(menu: MenuConfig | undefined): DraftGroup[] {
  return (menu?.groups ?? []).map((g) => ({
    id: g.id,
    name: g.name,
    time: g.time ?? "",
    icon: g.icon,
    items: toDraftItems(g.items),
  }));
}

/** Stable string for dirty comparison (text trimmed). */
function serialize(groups: DraftGroup[]): string {
  return JSON.stringify(
    groups.map((g) => ({
      name: g.name.trim(),
      time: g.time.trim(),
      icon: g.icon,
      items: g.items.map((it) => ({
        name: it.name.trim(),
        description: it.description.trim(),
        dietary: it.dietary,
      })),
    })),
  );
}

/** Moves the element at `index` by `dir` (-1 up, +1 down), returns a new array. */
function move<T>(list: T[], index: number, dir: -1 | 1): T[] {
  const target = index + dir;
  if (index < 0 || index >= list.length) return list;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  const moved = next.splice(index, 1)[0] as T;
  next.splice(target, 0, moved);
  return next;
}

function IconPicker({
  value,
  onChange,
}: {
  value: MenuIconKey;
  onChange: (key: MenuIconKey) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const Current = getMenuIcon(value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="icon" aria-label={t("menuEditor.chooseIcon")}>
          <Current className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start">
        <div className="grid grid-cols-6 gap-1">
          {MENU_ICON_OPTIONS.map(({ key, labelKey, Icon }) => (
            <Button
              key={key}
              type="button"
              variant={key === value ? "default" : "ghost"}
              size="icon"
              title={t(labelKey)}
              aria-label={t(labelKey)}
              onClick={() => {
                onChange(key);
                setOpen(false);
              }}
            >
              <Icon className="h-4 w-4" />
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function GroupCard({
  group,
  open,
  canMoveUp,
  canMoveDown,
  onToggle,
  onChangeName,
  onChangeTime,
  onChangeIcon,
  onMove,
  onAddItem,
  onUpdateItem,
  onMoveItem,
  onRemoveItem,
  onRemoveGroup,
}: {
  group: DraftGroup;
  open: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onToggle: () => void;
  onChangeName: (name: string) => void;
  onChangeTime: (time: string) => void;
  onChangeIcon: (icon: MenuIconKey) => void;
  onMove: (dir: -1 | 1) => void;
  onAddItem: () => void;
  onUpdateItem: (itemId: string, patch: Partial<DraftItem>) => void;
  onMoveItem: (itemId: string, dir: -1 | 1) => void;
  onRemoveItem: (itemId: string) => void;
  onRemoveGroup: () => void;
}) {
  const { t } = useTranslation();
  const Icon = getMenuIcon(group.icon);
  const itemCount = group.items.length;

  return (
    <div className="rounded-md border">
      <div className="flex items-center gap-1 p-3">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          onClick={onToggle}
          aria-expanded={open}
        >
          {open ? (
            <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium">
              {group.name.trim() || t("menuEditor.untitledGroup")}
              {group.time.trim() ? `, ${group.time.trim()}` : ""}
            </span>
            <span className="block text-xs text-muted-foreground">
              {itemCount === 0 ? t("menuEditor.noItems") : t("menuEditor.items", { count: itemCount })}
            </span>
          </span>
        </button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          aria-label={t("menuEditor.moveGroupUp")}
          disabled={!canMoveUp}
          onClick={() => onMove(-1)}
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          aria-label={t("menuEditor.moveGroupDown")}
          disabled={!canMoveDown}
          onClick={() => onMove(1)}
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          aria-label={t("menuEditor.removeGroup")}
          onClick={onRemoveGroup}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {open && (
        <div className="space-y-4 border-t p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>{t("menuEditor.groupName")}</Label>
              <div className="flex items-center gap-2">
                <IconPicker value={group.icon} onChange={onChangeIcon} />
                <Input
                  placeholder={t("menuEditor.groupNamePlaceholder")}
                  value={group.name}
                  onChange={(e) => onChangeName(e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("menuEditor.timeOptional")}</Label>
              <Input
                placeholder={t("menuEditor.timePlaceholder")}
                value={group.time}
                onChange={(e) => onChangeTime(e.target.value)}
              />
            </div>
          </div>

          {group.items.length === 0 && (
            <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
              {t("menuEditor.noItemsInGroup")}
            </p>
          )}
          {group.items.map((it, index) => (
            <div key={it.id} className="space-y-2 rounded-md border p-3">
              <div className="flex items-center gap-1">
                <Input
                  placeholder={t("menuEditor.itemNamePlaceholder")}
                  value={it.name}
                  onChange={(e) => onUpdateItem(it.id, { name: e.target.value })}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  aria-label={t("menuEditor.moveItemUp")}
                  disabled={index === 0}
                  onClick={() => onMoveItem(it.id, -1)}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  aria-label={t("menuEditor.moveItemDown")}
                  disabled={index === group.items.length - 1}
                  onClick={() => onMoveItem(it.id, 1)}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  aria-label={t("menuEditor.removeItem")}
                  onClick={() => onRemoveItem(it.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <Input
                placeholder={t("menuEditor.descriptionPlaceholder")}
                value={it.description}
                onChange={(e) =>
                  onUpdateItem(it.id, { description: e.target.value })
                }
              />
              <DietaryChips
                value={it.dietary}
                onChange={(dietary) => onUpdateItem(it.id, { dietary })}
              />
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={onAddItem}
          >
            <Plus className="h-4 w-4" /> {t("menuEditor.addItem")}
          </Button>
        </div>
      )}
    </div>
  );
}

export function MenuSection({
  siteId,
  menu,
  allowMenu,
}: {
  siteId: string;
  menu?: MenuConfig;
  allowMenu: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());

  const initialGroups = useMemo(() => toDraftGroups(menu), [menu]);
  const [draftGroups, setDraftGroups] = useState<DraftGroup[]>(initialGroups);

  const totalItems = draftGroups.reduce((n, g) => n + g.items.length, 0);

  const mutation = useMutation({
    mutationFn: () => {
      const groups = draftGroups.map((g) => ({
        id: g.id,
        name: g.name.trim(),
        ...(g.time.trim() ? { time: g.time.trim() } : {}),
        icon: g.icon,
        items: g.items.map((it) => ({
          id: it.id,
          name: it.name.trim(),
          ...(it.description.trim() ? { description: it.description.trim() } : {}),
          ...(it.dietary.length ? { dietary: it.dietary } : {}),
        })),
      }));
      const payload: MenuConfig = { groups };
      return updateSiteConfig({ siteId, configPatch: { menu: payload } });
    },
    onSuccess: async () => {
      toast.success(t("menuEditor.updated"));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["site", siteId] }),
        queryClient.invalidateQueries({ queryKey: ["sites"] }),
      ]);
      setOpen(false);
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  const openDialog = () => {
    const groups = toDraftGroups(menu);
    setDraftGroups(groups);
    setOpenGroups(new Set(groups.length <= 1 ? groups.map((g) => g.id) : []));
    setOpen(true);
  };

  const toggleGroup = (id: string) =>
    setOpenGroups((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const addGroup = () => {
    const group = blankGroup();
    setDraftGroups((prev) => [...prev, group]);
    setOpenGroups((s) => new Set(s).add(group.id));
  };
  const removeGroup = (groupId: string) => {
    setDraftGroups((prev) => prev.filter((g) => g.id !== groupId));
    setOpenGroups((s) => {
      const next = new Set(s);
      next.delete(groupId);
      return next;
    });
  };
  const moveGroup = (groupId: string, dir: -1 | 1) =>
    setDraftGroups((prev) => {
      const index = prev.findIndex((g) => g.id === groupId);
      return index === -1 ? prev : move(prev, index, dir);
    });
  const patchGroup = (
    groupId: string,
    patch: Partial<Omit<DraftGroup, "items" | "id">>,
  ) =>
    setDraftGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, ...patch } : g)),
    );
  const addItem = (groupId: string) =>
    setDraftGroups((prev) =>
      prev.map((g) =>
        g.id === groupId ? { ...g, items: [...g.items, blankItem()] } : g,
      ),
    );
  const removeItem = (groupId: string, itemId: string) =>
    setDraftGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? { ...g, items: g.items.filter((it) => it.id !== itemId) }
          : g,
      ),
    );
  const moveItem = (groupId: string, itemId: string, dir: -1 | 1) =>
    setDraftGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        const index = g.items.findIndex((it) => it.id === itemId);
        return index === -1 ? g : { ...g, items: move(g.items, index, dir) };
      }),
    );
  const updateItem = (
    groupId: string,
    itemId: string,
    patch: Partial<DraftItem>,
  ) =>
    setDraftGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? {
              ...g,
              items: g.items.map((it) =>
                it.id === itemId ? { ...it, ...patch } : it,
              ),
            }
          : g,
      ),
    );

  const valid =
    totalItems <= MENU_MAX_ITEMS &&
    draftGroups.length <= MENU_MAX_GROUPS &&
    draftGroups.every(
      (g) =>
        g.name.trim() !== "" && g.items.every((it) => it.name.trim() !== ""),
    );
  const dirty = serialize(draftGroups) !== serialize(initialGroups);

  const summaryGroups = initialGroups.length;
  const summaryItems = initialGroups.reduce((n, g) => n + g.items.length, 0);

  return (
    <>
      <SettingRow
        title={t("menuEditor.title")}
        description={t("menuEditor.desc")}
        action={
          <Button type="button" variant="ghost" size="sm" onClick={openDialog}>
            {t("common.edit")}
          </Button>
        }
        value={
          <div className="space-y-1">
            <p>
              {t("menuEditor.title")}:{" "}
              <span className="font-medium">
                {allowMenu ? t("siteDetail.permissions.on") : t("siteDetail.permissions.off")}
              </span>
              {!allowMenu && (
                <span className="text-muted-foreground"> ({t("menuEditor.turnOnHint")})</span>
              )}
            </p>
            <p className="text-muted-foreground">
              {summaryItems === 0
                ? t("menuEditor.noItemsYet")
                : t("menuEditor.items", { count: summaryItems })}
              {summaryGroups > 0 ? `, ${t("menuEditor.groups", { count: summaryGroups })}` : ""}
            </p>
          </div>
        }
      />
      <EditDialog
        open={open}
        onOpenChange={setOpen}
        title={t("menuEditor.editTitle")}
        description={t("menuEditor.editDesc")}
        onSave={() => mutation.mutate()}
        saving={mutation.isPending}
        saveDisabled={!dirty || !valid}
        contentClassName="sm:max-w-2xl"
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>{t("menuEditor.groupsLabel")}</Label>
            <span className="text-xs text-muted-foreground">
              {t("menuEditor.itemsCount", { count: totalItems, max: MENU_MAX_ITEMS })}
            </span>
          </div>
          {draftGroups.length === 0 && (
            <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
              {t("menuEditor.noGroups")}
            </p>
          )}
          {draftGroups.map((group, index) => (
            <GroupCard
              key={group.id}
              group={group}
              open={openGroups.has(group.id)}
              canMoveUp={index > 0}
              canMoveDown={index < draftGroups.length - 1}
              onToggle={() => toggleGroup(group.id)}
              onChangeName={(name) => patchGroup(group.id, { name })}
              onChangeTime={(time) => patchGroup(group.id, { time })}
              onChangeIcon={(icon) => patchGroup(group.id, { icon })}
              onMove={(dir) => moveGroup(group.id, dir)}
              onAddItem={() => addItem(group.id)}
              onUpdateItem={(itemId, patch) => updateItem(group.id, itemId, patch)}
              onMoveItem={(itemId, dir) => moveItem(group.id, itemId, dir)}
              onRemoveItem={(itemId) => removeItem(group.id, itemId)}
              onRemoveGroup={() => removeGroup(group.id)}
            />
          ))}
          {draftGroups.length < MENU_MAX_GROUPS && (
            <Button type="button" variant="outline" className="w-full" onClick={addGroup}>
              <Plus className="h-4 w-4" /> {t("menuEditor.addGroup")}
            </Button>
          )}
        </div>
      </EditDialog>
    </>
  );
}
